import {Fragment} from 'react'
import type {ToolGroupingProps, ToolGroup, EntryWithContext} from './types'
import {ToolGroupRenderer} from './ToolGroupRenderer'
import {EDIT_TOOLS} from '../../../constants/tool-names'

/**
 * Generate a unique ID for a log entry for use in standalone groups
 */
function getItemId(item: EntryWithContext): string {
  if (item.type === 'choice') {
    return `${item.entryId}-${item.choiceIndex}`
  }
  return `other-${item.index}`
}

export function ToolGrouping({entries, groupingFn, groupHeaderFn, children}: ToolGroupingProps) {
  // Process log entries and create groups
  const result: ToolGroup[] = []
  let lastGroup: ToolGroup | null = null

  for (const item of entries) {
    const groupingResult = groupingFn(item, lastGroup)

    // Check if we should add to the existing group based on matching groupId
    const shouldAddToExistingGroup =
      groupingResult.shouldGroup &&
      groupingResult.groupId &&
      lastGroup &&
      !lastGroup.metadata?.standalone &&
      lastGroup.id === groupingResult.groupId

    if (shouldAddToExistingGroup && lastGroup) {
      lastGroup.entries.push(item)
      lastGroup.count = lastGroup.entries.length
      if (groupingResult.groupMetadata) {
        lastGroup.metadata = {
          ...lastGroup.metadata,
          ...groupingResult.groupMetadata,
        }
      }
    } else if (groupingResult.shouldGroup && groupingResult.groupId) {
      // Start a new group using the groupId
      const newGroup: ToolGroup = {
        id: groupingResult.groupId,
        entries: [item],
        count: 1,
        metadata: groupingResult.groupMetadata,
      }
      result.push(newGroup)
      lastGroup = newGroup
    } else {
      // Standalone item - this breaks any active grouping
      const standaloneGroup: ToolGroup = {
        id: `standalone-${getItemId(item)}`,
        entries: [item],
        count: 1,
        metadata: {standalone: true},
      }
      result.push(standaloneGroup)
      lastGroup = standaloneGroup // Track it so groupingFn can check tool names
    }
  }

  return (
    <>
      {result.map(group => {
        const isStandalone = group.metadata?.standalone === true
        const firstItem = group.entries[0]

        const isSingleItemGroup = group.count === 1

        // Render standalone or size-1 groups directly without wrapper
        if ((isStandalone || isSingleItemGroup) && firstItem) {
          return <Fragment key={group.id}>{children(firstItem)}</Fragment>
        }

        // Render multi-item groups with header
        // Determine if this is an edit group based on the tool name in metadata
        const toolName = group.metadata?.toolName as string | undefined
        const isEditGroup = EDIT_TOOLS.includes(toolName as (typeof EDIT_TOOLS)[number])

        return (
          <ToolGroupRenderer key={group.id} group={group} groupHeaderFn={groupHeaderFn}>
            {group.entries.map(item =>
              children(item, {
                isInGroup: true,
                groupId: group.id,
                isEditGroup,
              }),
            )}
          </ToolGroupRenderer>
        )
      })}
    </>
  )
}
